/**
 * @file CoverSchemes.tsx
 * @description Общие примитивы мини-схем для карточек-вариантов `OptionCards`
 * панелей вида сайта (phase19.16; решения пользователя 2026-10-12 «Как
 * показать обложку» и «Нужна визуализация»): рамка `Scheme` 64×40, плашка
 * «фото» `Photo`, полоска «текст» `Line` и схема раскладки картинки шапки
 * `CoverLayoutScheme` (фоном / справа / слева).
 *
 * Нейтральный модуль: им пользуются и блог (`blog/BlogSchemes`, обложка
 * ленты), и каталог (`catalog/CategoryHeaderSchemes`, баннер категории) —
 * чтобы схемы панелей были одного вида и ни один раздел не зависел от другого.
 * Схемы нейтральные (цвет текста панели, без цветов сайта): фото — плотная
 * плашка, текст — полоски. Вариант схемы — запись реестра, а не ветка.
 */

import type { ReactNode } from "react";

import type { CoverLayout } from "../../../lib/site-view/cover-layout";

/** Плашка «фото». */
export const Photo = ({ x, y, w, h }: { x: number; y: number; w: number; h: number }) => (
  <rect x={x} y={y} width={w} height={h} rx={2} fill="currentColor" fillOpacity={0.35} />
);

/** Полоска «текст»; `light` — поверх фото. */
export const Line = ({ x, y, w, h = 2, light = false }: { x: number; y: number; w: number; h?: number; light?: boolean }) => (
  <rect x={x} y={y} width={w} height={h} rx={1} fill={light ? "var(--surface-lowest)" : "currentColor"} fillOpacity={light ? 1 : 0.6} />
);

/** Три строки шапки: надзаголовок, заголовок, подзаголовок — от левого края `x` или по центру `cx`. */
function HeaderText({ x, cx, y, w, light = false }: { x?: number; cx?: number; y: number; w: number; light?: boolean }) {
  const at = (width: number) => (cx !== undefined ? cx - width / 2 : (x ?? 0));
  return (
    <g>
      <Line x={at(w * 0.4)} y={y} w={w * 0.4} light={light} />
      <Line x={at(w)} y={y + 5} w={w} h={4} light={light} />
      <Line x={at(w * 0.75)} y={y + 12} w={w * 0.75} light={light} />
    </g>
  );
}

/** Схема 64×40 (на экране 77×48), общая рамка для всех вариантов. */
export function Scheme({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 64 40" className="h-12 w-[4.8rem] text-on-surface" aria-hidden>
      {children}
    </svg>
  );
}

const COVER: Readonly<Record<CoverLayout, ReactNode>> = {
  // Фото на всю ширину, текст по центру поверх.
  background: (
    <g>
      <Photo x={0} y={4} w={64} h={32} />
      <HeaderText cx={32} y={11} w={36} light />
    </g>
  ),
  // Текст слева, картинка — правые 40 %.
  "side-right": (
    <g>
      <HeaderText x={3} y={12} w={30} />
      <Photo x={38} y={6} w={24} h={28} />
    </g>
  ),
  "side-left": (
    <g>
      <Photo x={2} y={6} w={24} h={28} />
      <HeaderText x={31} y={12} w={30} />
    </g>
  ),
};

/** Раскладка картинки шапки: обложка ленты блога и баннер категории. */
export const CoverLayoutScheme = ({ layout }: { layout: CoverLayout }) => <Scheme>{COVER[layout]}</Scheme>;
